import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Reveal, { RevealWords } from '../components/Reveal';
import Locations from '../components/Locations';
import Marquee from '../components/Marquee';
import OrderBand from '../components/OrderBand';
import TapeLabel from '../components/TapeLabel';
import { ArrowRight } from '../components/Icons';
import { COPY, SITE, MARQUEE } from '../data/site';
import { EVENTS } from '../data/events';
import { PHOTO, TEXTURE } from '../data/media';
import { useScrollY } from '../hooks/useReveal';
import './Home.css';

/* Mirrors wethelocal.co's homepage section-for-section:
   hero → tagline statement → Our locations → "We celebrate…" → What's on. */

function Hero() {
  const y = useScrollY();
  const shift = Math.min(y * 0.22, 160);

  return (
    <header className="hero">
      <div className="hero__media" aria-hidden="true">
        <img
          src={PHOTO.plate7}
          alt=""
          style={{ transform: `translate3d(0, ${shift}px, 0) scale(1.12)` }}
        />
        <div className="hero__veil" />
      </div>

      <div className="page hero__inner on-dark">
        <h1 className="display brand d1 hero__title">
          <RevealWords text={SITE.tagline} step={0.075} />
        </h1>

        <Reveal className="hero__cta" delay={0.6}>
          <a href="#locations" className="btn btn--gold hero__loclink">
            <span>View locations</span>
            <ArrowRight />
          </a>
        </Reveal>
      </div>
    </header>
  );
}

/* Olive marker tagline on the left, menu philosophy + About link on the right —
   exactly the two-column block that follows the hero on the WordPress site. */
function Statement() {
  return (
    <section className="section statement">
      <div className="page split split--wide-left statement__grid">
        <Reveal as="p" className="display brand statement__lead">
          {COPY.heroLead}
        </Reveal>
        <div className="stack stack-md">
          <Reveal as="p" className="body" delay={0.1}>
            {COPY.introBody}
          </Reveal>
          <Reveal delay={0.18}>
            <Link to="/about" className="link">
              About The Local <ArrowRight />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* "We celebrate good food and easy moments…" — marker heading + kitchen copy
   left, a stack of dishes right over the menu-bg line art. */
function Celebrate() {
  return (
    <section className="section celebrate">
      <div className="page split split--wide-right celebrate__grid">
        <div className="stack stack-md celebrate__copy">
          <Reveal as="h2" className="display brand celebrate__title">
            {COPY.introHeading}
          </Reveal>
          <Reveal as="p" className="body" delay={0.08}>
            {COPY.philosophyBody}
          </Reveal>
          <Reveal delay={0.16}>
            <Link to="/menus" className="btn btn--gold">
              <span>View menus</span>
              <ArrowRight />
            </Link>
          </Reveal>
        </div>

        <div className="celebrate__stack">
          {[PHOTO.plate1, PHOTO.plate4, PHOTO.margherita].map((src, i) => (
            <Reveal key={src} variant="zoom" delay={(i % 3) * 0.08}>
              <div className="frame frame--3x2 frame--hover celebrate__img">
                <img src={src} alt="" loading="lazy" />
              </div>
            </Reveal>
          ))}
          <Reveal variant="zoom" delay={0.08}>
            <Link
              to="/menus"
              className="frame frame--4x5 frame--hover celebrate__img celebrate__cover"
              aria-label="View the menus"
            >
              <img src={TEXTURE.menuBg} alt="The Local menu cover" loading="lazy" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhatsOnStrip() {
  const today = new Date().getDay();

  return (
    <section className="section whatson-strip">
      <div className="page stack stack-lg">
        <TapeLabel>What&apos;s on</TapeLabel>

        <ul className="whatson-strip__grid">
          {EVENTS.map((ev, i) => {
            const on = ev.day.includes(today);
            return (
              <Reveal as="li" key={ev.id} delay={i * 0.09}>
                <Link to={`/whats-on/${ev.id}`} className={`evcard${on ? ' is-today' : ''}`}>
                  <div className="frame frame--4x5 frame--hover evcard__media">
                    <img src={ev.image} alt="" loading="lazy" />
                    {on ? <span className="evcard__today">On today</span> : null}
                  </div>
                  <div className="evcard__body">
                    <h3 className="display brand evcard__name">{ev.name}</h3>
                    <p className="evcard__when tabular">
                      {ev.when} · {ev.time}
                    </p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </ul>

        <Reveal className="whatson-strip__more">
          <Link to="/whats-on" className="link">
            See the full week <ArrowRight />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Every dish is a moment shared"
        description={SITE.shortDescription}
        path="/"
      />
      <Hero />
      <Marquee items={MARQUEE} />
      <Statement />
      <Locations />
      <Celebrate />
      <WhatsOnStrip />
      <OrderBand />
    </>
  );
}
